import type { Experiment, Variant } from 'types';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useState } from 'react';
import { Scale } from 'lucide-react';
import { toast } from 'sonner';
import { type WeightEntry, evenDistribution, redistributeWeights, resolveWeights } from 'helpers/weight-distribution';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useVariants } from '@/state/experiments/useVariants';

type EditWeightsModalProps = {
  experiment: Experiment;
  variants: Variant[];
  open: boolean;
  onClose: () => void;
};

/** The control is the neutral series, so its chip stays uncoloured. */
const CONTROL_CHIP_CLASS =
  'border-line bg-field text-dim shrink-0 rounded-pill border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase';

/**
 * Percent (user-facing, up to 2 decimals) to basis points. Rounding to one
 * decimal place of basis points first keeps the result decimal-correct:
 * 33.335 * 100 is 3333.4999… in binary, which a bare Math.round truncates
 * to 3333 instead of the 3334 the typed decimal means.
 */
const toBasisPoints = (percent: number) => Math.round(Math.round(percent * 1000) / 10);

/**
 * Stored weights may be floating (null, drafts) or — for legacy data — sum to
 * something other than 100%. Floating weights resolve to their even share; a
 * distribution too broken to resolve resets to an even split, which is also
 * what the old data (duplicates stamped with a deploy weight) actually meant.
 * The caller shows a notice when the fallback was taken.
 */
function initialDistribution(variants: readonly Variant[]): WeightEntry[] {
  const resolved = resolveWeights(variants);
  if (resolved) return resolved;
  return evenDistribution(variants.map((variant) => variant.id));
}

export const EditWeightsModal = ({ experiment, variants, open, onClose }: EditWeightsModalProps) => {
  const { setWeights } = useVariants(experiment.id);
  const [weights, setLocalWeights] = useState<WeightEntry[]>(() => initialDistribution(variants));
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  const variantById = new Map(variants.map((variant) => [variant.id, variant] as const));
  const isRunning = experiment.status === 'Running';
  // The stored distribution could not be resolved — what's shown is a proposed
  // even split, not the current state, and the user deserves to know that.
  const showsRepairedSplit = resolveWeights(variants) === null;

  const applyChange = (changedId: number, newWeight: number) => {
    setLocalWeights((entries) => redistributeWeights({ entries, changedId, newWeight }));
  };

  const clearDraft = (id: number) => {
    setDrafts((prev) => {
      const { [id]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const onSliderChange = (id: number) => (value: number[]) => {
    clearDraft(id);
    applyChange(id, toBasisPoints(value[0] ?? 0));
  };

  const onInputChange = (id: number) => (ev: ChangeEvent<HTMLInputElement>) => {
    setDrafts((prev) => ({ ...prev, [id]: ev.target.value }));
  };

  const onInputBlur = (id: number) => () => {
    const draft = drafts[id];
    if (draft === undefined) return;
    const parsed = parseFloat(draft);
    if (!isNaN(parsed)) applyChange(id, toBasisPoints(parsed));
    clearDraft(id);
  };

  const onInputKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      ev.currentTarget.blur();
    }
  };

  const onEvenSplit = () => {
    setDrafts({});
    setLocalWeights(evenDistribution(weights.map((entry) => entry.id)));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await setWeights(weights);
      onClose();
    } catch (ex) {
      // The server's 400/409 messages are written to be user-facing; nothing
      // else in the dashboard renders them.
      const message = (ex as { message?: string })?.message;
      toast.error(message || 'Failed to save weights — please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !saving && onClose()}>
      <DialogContent className="border-line bg-panel sm:rounded-panel sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">Edit Weights</DialogTitle>
          <DialogDescription className="supporting">
            {isRunning
              ? 'Adjusting one variant rebalances the others proportionally. Changes reach new visitors as soon as you save.'
              : 'Adjusting one variant rebalances the others proportionally. This split takes effect when the experiment starts.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {showsRepairedSplit && (
            <div className="border-warn-line rounded-card border p-3">
              <p className="micro-label text-warn">weights repaired</p>
              <p className="supporting mt-1">
                The stored weights were invalid and could not be shown as saved. This is a proposed even split — it is
                applied only if you save.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={onEvenSplit}
              className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
            >
              <Scale className="size-3.5" strokeWidth={1.5} />
              Even split
            </Button>
          </div>

          {weights.map((entry) => {
            const variant = variantById.get(entry.id);
            const isControl = experiment.controlVariantId
              ? entry.id === experiment.controlVariantId
              : variant?.name === 'Original';
            const percent = entry.weight / 100;

            return (
              <div key={entry.id} className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-foreground truncate text-body font-medium">
                      {variant?.name ?? `Variant ${entry.id}`}
                    </span>
                    {isControl && <span className={CONTROL_CHIP_CLASS}>control</span>}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Input
                      type="number"
                      className="border-line bg-field text-foreground num w-24 rounded-lg text-right"
                      value={drafts[entry.id] ?? percent.toFixed(2)}
                      onChange={onInputChange(entry.id)}
                      onBlur={onInputBlur(entry.id)}
                      onKeyDown={onInputKeyDown}
                      step={0.01}
                      min={0}
                      max={100}
                    />
                    <span className="text-dim num">%</span>
                  </div>
                </div>
                <Slider
                  variant="default"
                  value={[percent]}
                  min={0}
                  max={100}
                  step={0.1}
                  onValueChange={onSliderChange(entry.id)}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
